import './HistoricalPercentageChangeWidgetGrid.scss';
import React, { useContext, useMemo } from 'react';
import AppContext from 'app/AppContext.tsx';
import {
    GetRowClassParams,
    Grid,
    GridApi,
} from '@instinet-analytics/semantic-ui-components';

import { useWidgetGridState } from 'widgets/store/grid/useWidgetGridState.ts';

import { useHistoricalPercentageChangeData } from 'widgets/historicalPercentageChange/data/useHistoricalPercentageChangeData.ts';
import {
    getHistoricalPercentageChangeWidgetColDefs,
    HistoricalPercentageChangeWidgetGridColumn,
    historicalPercentageChangeWidgetGridColumnsDefinitionDictionary,
} from 'widgets/historicalPercentageChange/widget/components/grid/columns.ts';
import { HistoricalPercentageChangeDataItem } from 'widgets/historicalPercentageChange/data/historicalPercentageChangeDataItem.ts';
import { HistPriceVolDataState } from 'widgets/historicalPercentageChange/store/data/HistoricalPercentageChangeDataState.ts';
import { useHistoricalPercentageChangeCapsuleConfigSlice } from 'widgets/historicalPercentageChange/capsule/store.ts';

const rowHeight = 25;

export function HistoricalPercentageChangeWidgetGrid({
    height,
    onGridApiSet,
    streamData,
}: {
    height: number;
    onGridApiSet: (gridApi: GridApi) => void;
    streamData: HistPriceVolDataState;
}) {
    const data = useHistoricalPercentageChangeData(streamData);
    const {
        config: { periodQty },
    } = useHistoricalPercentageChangeCapsuleConfigSlice();
    const items = data.slice(data.length - periodQty);
    const context = useContext(AppContext);

    const colDefs = useMemo(
        () => getHistoricalPercentageChangeWidgetColDefs(context),
        [context]
    );

    const { gridPropsFromState, columnDefs } = useWidgetGridState<
        HistoricalPercentageChangeDataItem,
        HistoricalPercentageChangeWidgetGridColumn
    >(historicalPercentageChangeWidgetGridColumnsDefinitionDictionary, colDefs);

    const onGridReady = (api: GridApi) => {
        onGridApiSet(api);
    };

    const getRowClassRules = {
        negative: (
            item: GetRowClassParams<HistoricalPercentageChangeDataItem>
        ) => (item.data.percentChange ?? 0) < 0,
        positive: (
            item: GetRowClassParams<HistoricalPercentageChangeDataItem>
        ) => (item.data.percentChange ?? 0) > 0,
    };

    return (
        <div
            className="HistoricalPercentageChangeWidgetGrid"
            style={{ height }}
        >
            <Grid
                className={
                    'historical-percentage-chart-widget-grid ag-theme-balham-dark'
                }
                rowHeight={rowHeight}
                headerHeight={rowHeight}
                columnDefs={columnDefs}
                customStyling={true}
                rowData={items}
                rowClassRules={getRowClassRules}
                getRowDataId={(d: HistoricalPercentageChangeDataItem) =>
                    d.date.toString()
                }
                suppressColumnVirtualisation={false}
                onGridReady={onGridReady}
                {...gridPropsFromState}
            />
        </div>
    );
}
